/** Global hosts: confirmation modal (two-step tool confirmation) and toasts. Mounted once in the app root. */
import { AlertTriangle, CheckCircle2, Info, ShieldAlert, X, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { isDestructiveTool, subscribeConfirmations, type PendingConfirmation } from "@/api/confirm-store";
import { humanTool } from "@/api/hooks";
import { dismissToast, subscribeToasts, type Toast } from "@/api/toast-store";
import { cn } from "@/lib/cn";
import { safeInAppHref } from "@/lib/safe-url";
import { Button } from "../ui/Button";
import { Modal } from "../ui/Overlay";

export function ConfirmationHost() {
  const [queue, setQueue] = useState<PendingConfirmation[]>([]);
  useEffect(() => subscribeConfirmations(setQueue), []);
  const current = queue[0];
  const [showInput, setShowInput] = useState(false);
  const [typedName, setTypedName] = useState("");
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!current?.expiresAt) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [current?.expiresAt]);
  useEffect(() => { setShowInput(false); setTypedName(""); }, [current?.id]);
  if (!current) return null;

  const destructive = isDestructiveTool(current.tool);
  const deleteName = current.tool === "brands.delete" ? String(current.input?.confirmName ?? "") : null;
  const expires = current.expiresAt ? new Date(current.expiresAt).getTime() - now : null;
  const expired = expires !== null && expires <= 0;
  const mins = expires !== null ? Math.max(0, Math.floor(expires / 60000)) : 0;
  const secs = expires !== null ? Math.max(0, Math.floor((expires % 60000) / 1000)) : 0;

  return (
    <Modal
      open
      onClose={() => current.resolve(false)}
      size="sm"
      title={
        <span className="flex items-center gap-2.5">
          <span className={cn("grid size-8 place-items-center rounded-full", destructive ? "bg-danger-soft text-danger" : "bg-primary-soft text-lavender")}>
            <ShieldAlert className="size-4" />
          </span>
          {current.label ?? `Confirm: ${humanTool(current.tool)}`}
        </span>
      }
      footer={
        <>
          <Button variant="ghost" onClick={() => current.resolve(false)}>
            Cancel
          </Button>
          <Button variant={destructive ? "danger" : "primary"} disabled={expired || (deleteName !== null && (!deleteName || typedName !== deleteName))} onClick={() => current.resolve(true)} autoFocus data-testid="confirm-action">
            {deleteName !== null ? "Permanently delete brand" : destructive ? "Yes, do it" : "Confirm"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="whitespace-pre-wrap text-[14px] leading-relaxed text-fg" data-testid="confirm-summary">
          {current.summary}
        </p>
        <div className="flex flex-wrap items-center gap-2 text-[11.5px] text-muted">
          <code className="rounded-md bg-surface-2 px-1.5 py-0.5 font-mono text-[11px] text-fg/80">{current.tool}</code>
          {expires !== null && (
            <span className={cn(expired && "text-danger")}>{expired ? "Confirmation expired — run it again." : `Token expires in ${mins}:${String(secs).padStart(2, "0")}`}</span>
          )}
        </div>
        {deleteName !== null && <label className="flex flex-col gap-2 text-sm">
          Type {deleteName} to confirm permanent deletion
          <input aria-label={`Type ${deleteName} to confirm permanent deletion`} autoComplete="off" value={typedName} onChange={e => setTypedName(e.target.value)} className="rounded-lg border border-line bg-surface-3 p-2 text-fg" autoFocus />
        </label>}
        {current.input && Object.keys(current.input).length > 0 && (
          <div>
            <button type="button" className="text-xs text-link hover:text-lavender" onClick={() => setShowInput((v) => !v)}>
              {showInput ? "Hide exact request" : "Show exact request"}
            </button>
            {showInput && (
              <pre className="mt-2 max-h-48 overflow-auto rounded-lg border border-line bg-surface-3 p-3 font-mono text-[11px] leading-relaxed text-muted scrollbar-thin">
                {JSON.stringify(current.input, null, 2)}
              </pre>
            )}
          </div>
        )}
        <p className="text-[11.5px] text-subtle">The confirmation is single-use and bound to this exact request. Everything is logged to the audit chain.</p>
        {queue.length > 1 && <p className="text-[11.5px] text-muted">{queue.length - 1} more waiting for confirmation</p>}
      </div>
    </Modal>
  );
}

const toneIcon = {
  neutral: Info,
  success: CheckCircle2,
  danger: XCircle,
  warning: AlertTriangle,
};
const toneColor = {
  neutral: "text-muted",
  success: "text-success",
  danger: "text-danger",
  warning: "text-amber",
};

export function Toaster() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  useEffect(() => subscribeToasts(setToasts), []);
  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[110] flex w-[min(380px,calc(100vw-2rem))] flex-col gap-2" aria-live="polite">
      {toasts.map((t) => {
        const tone = t.tone ?? "neutral";
        const Icon = toneIcon[tone];
        // Toast links can carry server-supplied hrefs: only same-origin in-app paths become a <Link>.
        const actionHref = t.action?.href ? safeInAppHref(t.action.href) : null;
        return (
          <div key={t.id} className="pointer-events-auto flex animate-pop-in items-start gap-3 rounded-xl border border-line-strong bg-[#1f1f22] p-3 pr-2 shadow-pop" role="status">
            <Icon className={cn("mt-0.5 size-4.5 shrink-0", toneColor[tone])} />
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold">{t.title}</div>
              {t.body && <div className="mt-0.5 text-[12.5px] leading-snug text-muted">{t.body}</div>}
              {t.action &&
                (t.action.href ? (
                  actionHref ? (
                    <Link to={actionHref} onClick={() => dismissToast(t.id)} className="mt-1.5 inline-block text-[12.5px] font-medium text-link hover:text-lavender">
                      {t.action.label} →
                    </Link>
                  ) : null
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      t.action?.onClick?.();
                      dismissToast(t.id);
                    }}
                    className="mt-1.5 text-[12.5px] font-medium text-link hover:text-lavender"
                  >
                    {t.action.label}
                  </button>
                ))}
            </div>
            <button type="button" onClick={() => dismissToast(t.id)} aria-label="Dismiss" className="grid size-6 place-items-center rounded-md text-subtle hover:bg-hover hover:text-fg">
              <X className="size-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
